import React, { useCallback, useEffect, useMemo, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import Seo from "../../components/Seo";
import { LEGAL_PAGE_SEO } from "../../lib/seo";
import {
  clearCognitoSession,
  getCognitoSessionAccessToken,
  isCognitoAdminAuthConfigured,
  isCognitoAdminAuthMode,
  isCognitoSessionExpired,
  persistCognitoSession,
  readStoredCognitoSession,
  refreshCognitoSession,
  signInWithCognito
} from "./admin-auth";
import { persistToken, readStoredToken } from "./admin-utils";
import "../Shop.css";

const AdminLayout = () => {
  const cognitoMode = isCognitoAdminAuthMode();
  const [adminToken, setAdminToken] = useState(() => {
    if (!cognitoMode) {
      return readStoredToken();
    }

    const storedSession = readStoredCognitoSession();
    return getCognitoSessionAccessToken(storedSession);
  });
  const [tokenInput, setTokenInput] = useState(() => (cognitoMode ? "" : readStoredToken()));
  const [usernameInput, setUsernameInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [adminUsername, setAdminUsername] = useState(() => {
    const storedSession = readStoredCognitoSession();
    return storedSession?.username || "";
  });
  const [authError, setAuthError] = useState("");
  const [isAuthLoading, setIsAuthLoading] = useState(cognitoMode);
  const [isAuthSubmitting, setIsAuthSubmitting] = useState(false);

  const hasToken = Boolean(adminToken);
  const cognitoConfigured = !cognitoMode || isCognitoAdminAuthConfigured();

  useEffect(() => {
    if (!cognitoMode) { setIsAuthLoading(false); return; }
    let cancelled = false;
    let timer;
    let refreshing = false;
    const syncSession = async () => {
      if (refreshing) return;
      refreshing = true;
      window.clearTimeout(timer);
      try {
        let session = readStoredCognitoSession();
        if (!session) { setAdminToken(""); setAdminUsername(""); return; }
        if (isCognitoSessionExpired(session)) session = await refreshCognitoSession(session);
        if (cancelled) return;
        persistCognitoSession(session);
        setAdminToken(session.accessToken);
        setAdminUsername(session.username || "");
        timer = window.setTimeout(syncSession, Math.max(1000, session.expiresAt - Date.now() - 30_000));
      } catch {
        if (!cancelled) {
          clearCognitoSession();
          setAdminToken(""); setAdminUsername("");
          setAuthError("Sessione admin scaduta. Accedi di nuovo.");
        }
      } finally {
        refreshing = false;
        if (!cancelled) setIsAuthLoading(false);
      }
    };
    const onVisibility = () => { if (document.visibilityState === "visible") syncSession(); };
    syncSession();
    window.addEventListener("focus", syncSession);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      window.removeEventListener("focus", syncSession);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [cognitoMode, adminToken]);

  const handleLogout = useCallback(() => {
    persistToken("");
    clearCognitoSession();
    setAdminToken("");
    setTokenInput("");
    setUsernameInput("");
    setPasswordInput("");
    setAdminUsername("");
    setAuthError("");
  }, []);

  const handleUnauthorized = useCallback(() => {
    persistToken("");
    clearCognitoSession();
    setAdminToken("");
    setTokenInput("");
    setUsernameInput("");
    setPasswordInput("");
    setAdminUsername("");
    setAuthError(cognitoMode ? "Sessione admin non valida. Effettua di nuovo l'accesso." : "Token admin non valido.");
  }, [cognitoMode]);

  const handleTokenSubmit = (event) => {
    event.preventDefault();
    const cleanToken = tokenInput.trim();
    if (!cleanToken) {
      setAuthError("Inserisci un token admin valido.");
      return;
    }

    persistToken(cleanToken);
    setAdminToken(cleanToken);
    setAuthError("");
  };

  const handleCognitoSubmit = async (event) => {
    event.preventDefault();
    if (!cognitoConfigured) {
      setAuthError("Configurazione Cognito non disponibile in questo ambiente.");
      return;
    }

    setAuthError("");
    setIsAuthSubmitting(true);
    try {
      const session = await signInWithCognito({
        username: usernameInput,
        password: passwordInput
      });
      persistCognitoSession(session);
      setAdminToken(session.accessToken);
      setAdminUsername(session.username || "");
      setPasswordInput("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Login admin non riuscito.";
      setAuthError(message);
    } finally {
      setIsAuthSubmitting(false);
    }
  };

  const outletContext = useMemo(
    () => ({
      adminToken,
      handleUnauthorized
    }),
    [adminToken, handleUnauthorized]
  );

  if (isAuthLoading) {
    return (
      <section className="admin-page">
        <Seo
          title={LEGAL_PAGE_SEO.admin.title}
          description={LEGAL_PAGE_SEO.admin.description}
          path="/admin"
          robots="noindex,nofollow"
        />
        <h2 className="shop-title">Admin</h2>
        <p className="shop-subtitle">Verifica sessione admin...</p>
      </section>
    );
  }

  if (!hasToken) {
    return (
      <section className="admin-page">
        <Seo
          title={LEGAL_PAGE_SEO.admin.title}
          description={LEGAL_PAGE_SEO.admin.description}
          path="/admin"
          robots="noindex,nofollow"
        />
        <h2 className="shop-title">Admin</h2>
        <p className="shop-subtitle">
          {cognitoMode
            ? "Accedi con utente Cognito per gestire ordini, prodotti e menu pizzeria."
            : "Inserisci il token admin per gestire ordini, prodotti e menu pizzeria."}
        </p>
        {!cognitoConfigured && (
          <p className="shop-state shop-error">
            Configurazione Cognito assente. Verifica REACT_APP_COGNITO_REGION, REACT_APP_COGNITO_USER_POOL_ID e
            REACT_APP_COGNITO_CLIENT_ID.
          </p>
        )}

        {!cognitoMode ? (
          <form className="admin-login-form" onSubmit={handleTokenSubmit}>
            <label className="admin-form-label" htmlFor="admin-token-input">
              Admin API token
            </label>
            <input
              id="admin-token-input"
              className="admin-form-input"
              type="password"
              value={tokenInput}
              onChange={(event) => setTokenInput(event.target.value)}
              autoComplete="off"
              placeholder="Inserisci token x-admin-token"
            />
            <button type="submit" className="shop-add-button admin-submit-button">
              Accedi
            </button>
          </form>
        ) : (
          <form className="admin-login-form" onSubmit={handleCognitoSubmit}>
            <label className="admin-form-label" htmlFor="admin-email-input">
              Email admin
            </label>
            <input
              id="admin-email-input"
              className="admin-form-input"
              type="email"
              value={usernameInput}
              onChange={(event) => setUsernameInput(event.target.value)}
              autoComplete="username"
              placeholder="admin@esempio.it"
              required
              disabled={!cognitoConfigured}
            />
            <label className="admin-form-label" htmlFor="admin-password-input">
              Password
            </label>
            <input
              id="admin-password-input"
              className="admin-form-input"
              type="password"
              value={passwordInput}
              onChange={(event) => setPasswordInput(event.target.value)}
              autoComplete="current-password"
              placeholder="Inserisci password"
              required
              disabled={!cognitoConfigured}
            />
            <button
              type="submit"
              className="shop-add-button admin-submit-button"
              disabled={isAuthSubmitting || !cognitoConfigured}
            >
              {isAuthSubmitting ? "Accesso..." : "Accedi"}
            </button>
          </form>
        )}

        {authError && <p className="shop-state shop-error">{authError}</p>}
      </section>
    );
  }

  return (
    <section className="admin-page admin-workspace-shell">
      <Seo
        title={LEGAL_PAGE_SEO.admin.title}
        description={LEGAL_PAGE_SEO.admin.description}
        path="/admin"
        robots="noindex,nofollow"
      />
      <div className="admin-header-row">
        <div>
          <h2 className="shop-title">Admin</h2>
          <p className="shop-subtitle">
            Il lavoro del forno, in un unico posto.
            {cognitoMode && adminUsername ? ` Accesso: ${adminUsername}.` : ""}
          </p>
        </div>
        <div className="admin-header-actions">
          <button className="cart-remove-button" type="button" onClick={handleLogout}>
            Esci admin
          </button>
        </div>
      </div>

      <nav className="admin-nav" aria-label="Admin navigation">
        <NavLink
          to="orders"
          className={({ isActive }) =>
            isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
          }
        >
          Ordini shop
        </NavLink>
        <NavLink
          to="pizzeria-orders"
          className={({ isActive }) =>
            isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
          }
        >
          Richieste pizzeria
        </NavLink>
        <NavLink
          to="products"
          className={({ isActive }) =>
            isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
          }
        >
          Prodotti
        </NavLink>
        <NavLink
          to="menu"
          className={({ isActive }) =>
            isActive ? "admin-nav-link admin-nav-link-active" : "admin-nav-link"
          }
        >
          Menu pizzeria
        </NavLink>
      </nav>

      {authError && <p className="shop-state shop-error">{authError}</p>}

      <div className="admin-content-wrap">
        <Outlet context={outletContext} />
      </div>
    </section>
  );
};

export default AdminLayout;
